import { TAB, DEFAULT_ZOOM_DISPLAY_VALUE } from './dataMonitorUtil';

/**
 * 結合グラフのChart.jsコンポーネントを取得する
 * @param {object} graphMediator GraphMediator
 * @returns {object | null} Chart.jsコンポーネント、取得できない場合はnull
 */
export function getUnionGraphChart(graphMediator) {
  const graphElementList = graphMediator?.[TAB.UNION]?.graphElementList;
  const chart = graphElementList?.[0]?.getComponent?.();
  return chart || null;
}

/**
 * X軸の表示倍率からズーム範囲を算出する
 * @param {object} chart Chart.jsコンポーネント
 * @param {number} baseMin 基準の最小値
 * @param {number} baseMax 基準の最大値
 * @param {number} zoomRate 選択されたズーム倍率
 * @returns {{min: number, max: number} | null} 計算結果のズーム範囲、無効な場合はnull
 */
export function calculateZoomDisplayXRange(chart, baseMin, baseMax, zoomRate) {
  if (!Number.isFinite(baseMin) || !Number.isFinite(baseMax) || baseMin >= baseMax) {
    return null;
  }

  if (!Number.isFinite(zoomRate) || zoomRate <= 0) {
    return null;
  }

  const currentMin = chart?.scales?.x?.min;
  const currentMax = chart?.scales?.x?.max;
  const center = Number.isFinite(currentMin) && Number.isFinite(currentMax)
    ? (currentMin + currentMax) / 2
    : (baseMin + baseMax) / 2;

  const totalRange = baseMax - baseMin;
  const targetRange = Math.max(1, totalRange * (100 / zoomRate));
  const nextMin = center - targetRange / 2;
  const nextMax = center + targetRange / 2;

  return adjustZoomRange(nextMin, nextMax, baseMin, baseMax);
}

/**
 * 結合グラフのY軸表示倍率からズーム範囲を算出する
 * @param {object} chart Chart.jsコンポーネント
 * @param {number} zoomRate 選択されたズーム倍率
 * @returns {object | null} 計算結果のY軸ズーム範囲、無効な場合はnull
 */
export function calculateZoomDisplayYRanges(chart, zoomRate) {
  const scales = chart?.scales;
  if (!scales || !Number.isFinite(zoomRate) || zoomRate <= 0) {
    return null;
  }

  const initialScaleBounds = typeof chart?.getInitialScaleBounds === 'function'
    ? chart.getInitialScaleBounds()
    : null;

  const yRanges = {};
  Object.keys(scales)
    .filter(scaleId => scaleId.startsWith('yaxis_'))
    .forEach((scaleId) => {
      const currentMin = scales[scaleId]?.min;
      const currentMax = scales[scaleId]?.max;
      if (!Number.isFinite(currentMin) || !Number.isFinite(currentMax) || currentMin >= currentMax) {
        return;
      }

      // プルダウン操作は必ず100%の状態を基準として倍率を計算するため、初期のmin/maxを取得する。
      const initialMin = initialScaleBounds?.[scaleId]?.min;
      const initialMax = initialScaleBounds?.[scaleId]?.max;
      const baseMin = Number.isFinite(initialMin) ? initialMin : currentMin;
      const baseMax = Number.isFinite(initialMax) ? initialMax : currentMax;
      if (!Number.isFinite(baseMin) || !Number.isFinite(baseMax) || baseMin >= baseMax) {
        return;
      }

      const center = (currentMin + currentMax) / 2;
      const totalRange = baseMax - baseMin;
      const targetRange = Math.max(1, totalRange * (100 / zoomRate));
      const nextMin = center - targetRange / 2;
      const nextMax = center + targetRange / 2;
      const adjustedRange = adjustZoomRange(nextMin, nextMax, baseMin, baseMax);
      if (adjustedRange) {
        yRanges[scaleId] = adjustedRange;
      }
    });

  return Object.keys(yRanges).length > 0 ? yRanges : null;
}

/**
 * ズーム倍率プルダウン変更時の共通アクション
 * @param {object} dataMonitor 処理対象のコンポーネント
 * @param {String} value 選択されたズーム倍率の値
 */
export function applyZoomDisplayValueChangeAction(dataMonitor, value) {
  if (!dataMonitor.zoomDisplayOptions.includes(value)) {
    // 通常発生しないが選択肢に存在しない値が入ってきたときは処理を中断する
    return;
  }

  // 選択された値から%を除去して数値に変換する。例: "150%" -> 150
  const zoomRate = Number.parseInt(value, 10);

  const baseMin = dataMonitor.startTimeVal();
  const baseMax = dataMonitor.endTimeVal();
  const chart = getUnionGraphChart(dataMonitor.graphMediator);
  const xRange = calculateZoomDisplayXRange(chart, baseMin, baseMax, zoomRate);
  if (!xRange) {
    return;
  }

  const yRanges = calculateZoomDisplayYRanges(chart, zoomRate);

  dataMonitor.isApplyingZoomDisplayValue = true;
  dataMonitor.zoomDisplayValue = value;
  try {
    // グラフ同期メソッドを使ってプルダウンで選択した倍率を結合グラフに適用する
    // 分割グラフへはスクロール等の操作時と同様に、linkedMediatorsに対するグラフ同期機能呼び出しで倍率が反映される
    dataMonitor.graphMediator?.[TAB.UNION]?.syncZoomPanRange?.(null, xRange.min, xRange.max, yRanges);
  } finally {
    dataMonitor.isApplyingZoomDisplayValue = false;
  }
}

/**
 * ズームリセット押下時の共通アクション
 * @param {object} dataMonitor 処理対象のコンポーネント
 */
export function applyResetZoomAction(dataMonitor) {
  // リストタブが選択されている場合はリセット不可。
  // シークバーが操作不可状態のときもリセット不可な想定。
  if (dataMonitor.currentTab === TAB.LIST || dataMonitor.btnDisabled.seekBtns) {
    return;
  }

  dataMonitor.graphMediator[TAB.SPLIT]?.resetZoom?.();
  dataMonitor.graphMediator[TAB.UNION]?.resetZoom?.();

  dataMonitor.graphMediator[TAB.SPLIT]?.updateComponent?.();
  dataMonitor.graphMediator[TAB.UNION]?.updateComponent?.();

  dataMonitor.zoomDisplayValue = DEFAULT_ZOOM_DISPLAY_VALUE;
}

/**
 * プルダウン操作で求めた表示範囲が、グラフの表示範囲からはみ出さないように調整する
 * @param {number} nextMin プルダウン操作から求めた最小値
 * @param {number} nextMax プルダウン操作から求めた最大値
 * @param {number} baseMin グラフ本来の最小値
 * @param {number} baseMax グラフ本来の最大値
 * @returns {{min: number, max: number} | null} 調整結果のズーム範囲、無効な場合はnull
 */
function adjustZoomRange(nextMin, nextMax, baseMin, baseMax) {
  let adjustedMin = nextMin;
  let adjustedMax = nextMax;

  if (adjustedMin < baseMin) {
    adjustedMax += (baseMin - adjustedMin);
    adjustedMin = baseMin;
  }
  if (adjustedMax > baseMax) {
    adjustedMin -= (adjustedMax - baseMax);
    adjustedMax = baseMax;
  }

  adjustedMin = Math.max(baseMin, adjustedMin);
  adjustedMax = Math.min(baseMax, adjustedMax);
  if (!Number.isFinite(adjustedMin) || !Number.isFinite(adjustedMax) || adjustedMin >= adjustedMax) {
    return null;
  }

  return { min: adjustedMin, max: adjustedMax };
}

/**
 * ズーム/パン状態変更時のコールバックを生成する。
 * UDSとOBDで処理は同じのため共通化している。
 * @param {object} dataMonitor 処理対象のコンポーネント
 * @returns {Function} コールバック関数
 */
export function createZoomPanStateChangedCallback(dataMonitor) {
  return (isPan) => {
    if (!isPan && !dataMonitor.isApplyingZoomDisplayValue) {
      // 倍率変更プルダウンやパン操作以外の操作はデフォルト値(-%表示)に戻す
      dataMonitor.zoomDisplayValue = DEFAULT_ZOOM_DISPLAY_VALUE;
    }
    dataMonitor.$emit(
      'zoom-pan-state-changed',
      dataMonitor.isCurrentTabZoomedOrPanned(dataMonitor.currentTab)
    );
  };
}

/**
 * ズームリセットボタンの無効状態を判定する
 * @param {object} options 判定オプション
 * @param {boolean} options.inExecution 計測中フラグ
 * @param {boolean} options.inPlaying 再生中フラグ
 * @param {boolean} [options.hasCheckLines=true] グラフ項目有無
 * @param {boolean} [options.requireCheckLines=true] グラフ項目有無も判定するか
 * @param {number} options.currentTab 現在タブ
 * @returns {boolean} 無効な場合true
 */
export function isResetZoomDisabled({
  inExecution = false,
  inPlaying = false,
  hasCheckLines = true,
  requireCheckLines = true,
  currentTab,
}) {
  return Boolean(
    inExecution
    || inPlaying
    || (requireCheckLines && !hasCheckLines)
    || currentTab === TAB.LIST
  );
}

/**
 * ズームリセットを実行し、押下したボタンのフォーカスを外す
 * @param {object} dataMonitor リセット対象のコンポーネント
 * @param {object} event ボタンクリックイベント
 * @param {boolean} disabled ボタン無効状態
 */
export function handleResetZoomAction(dataMonitor, event, disabled = false) {
  if (disabled) {
    return;
  }

  dataMonitor?.handleResetZoom?.();
  event?.currentTarget?.blur?.();
}

/**
 * Options API向けのズーム/タブ変更ハンドラーを生成する
 * @param {object} options 設定
 * @param {string} [options.dataMonitorRefKey='data_monitor'] 対象ref名
 * @param {string} [options.currentTabKey='currentTab'] 現在タブのプロパティ名
 * @param {Function} [options.isResetZoomDisabled] リセット可否判定
 * @returns {object} methodsへ展開するハンドラー
 */
export function createZoomTabHandlersForOptions({
  dataMonitorRefKey = 'data_monitor',
  currentTabKey = 'currentTab',
  isResetZoomDisabled = () => false,
} = {}) {
  return {
    handleResetZoom(event) {
      handleResetZoomAction(this.$refs[dataMonitorRefKey], event, isResetZoomDisabled.call(this));
    },

    currentTabChanged(tab) {
      this[currentTabKey] = tab;
    },
  };
}

/**
 * Composition API向けのズーム/タブ変更ハンドラーを生成する
 * @param {object} dataMonitorRef DataMonitorコンポーネントのref
 * @param {object} currentTabRef 現在タブのref
 * @param {Function} [isResetZoomDisabled] リセット可否判定
 * @returns {object} setupのreturnへ展開するハンドラー
 */
export function createZoomTabHandlersForRefs(dataMonitorRef, currentTabRef, isResetZoomDisabled = () => false) {
  return {
    handleResetZoom(event) {
      handleResetZoomAction(dataMonitorRef.value, event, isResetZoomDisabled());
    },

    currentTabChanged(tab) {
      currentTabRef.value = tab;
    },
  };
}

/**
 * 倍率変更プルダウンの表示有無を切り替える
 * @param {number} currentTab 選択中のタブ
 * @param {boolean} inExecution 計測中フラグ
 * @param {boolean} inPlaying 再生中フラグ
 * @param {boolean} isWsExe WS.22/45/125の計測画面か否か
 * @returns {boolean} 倍率変更プルダウンを表示する場合はtrue、非表示の場合はfalseを返す。
 */
export function enableZoomControlSelect(currentTab, inExecution, inPlaying, isWsExe) {
  if (currentTab !== TAB.UNION) {
    // 結合グラフのみ表示する
    return false;
  }

  if (inExecution || inPlaying) {
    // 計測中・再生中は非表示にする
    return false;
  }

  // WS.22/45/125の計測画面は非表示にする
  return !isWsExe;
}